// La page d'administration (issue #95), servie par le service `admin-ui` seul.
//
// En français seulement, comme le vivier : elle n'est servie qu'à l'exploitant,
// jamais à un compte Radar anglophone (cf. `i18nSweep.test.ts`). Tout ce qu'elle
// affiche vient de `/api/admin/*` ; tout geste y est journalisé côté serveur, et
// l'onglet « Journal » le relit.
//
// L'onglet par défaut est « Serveur » : un déploiement bloqué par un arbre sale
// ne se voit nulle part ailleurs, et c'est la panne la plus coûteuse à rater.

import { useEffect, useState } from 'react'
import { Copy, LogOut, RefreshCw } from 'lucide-react'
import { get, post } from '../lib/api'
import { dateHeure, fullDate, relTime } from '../lib/format'
import { ActionButton, Empty, Loading, Modal, useData, useToast } from '../components/common'
import { Usage } from './AdminUsage'

const ONGLETS = [
  ['serveur', 'Serveur'],
  ['comptes', 'Comptes'],
  ['invitations', 'Invitations'],
  ['usage', 'Usage'],
  ['radar', 'Activité Radar'],
  ['corpus', 'Corpus et runs'],
  ['couts', 'Appels IA'],
  ['evenements', 'Événements'],
  ['paiements', 'Paiements'],
  ['journal', 'Journal'],
] as const
type Onglet = (typeof ONGLETS)[number][0]

function ongletDeLAdresse(): Onglet {
  const h = window.location.hash.replace(/^#\/?/, '').split('?')[0]
  return (ONGLETS.find(([k]) => k === h)?.[0] ?? 'serveur') as Onglet
}

export function Admin({ email, onDeconnexion }: { email: string; onDeconnexion: () => void }) {
  const [onglet, setOnglet] = useState<Onglet>(ongletDeLAdresse)
  useEffect(() => {
    const suivre = () => setOnglet(ongletDeLAdresse())
    window.addEventListener('hashchange', suivre)
    return () => window.removeEventListener('hashchange', suivre)
  }, [])
  return (
    <div className="adm">
      <header className="adm-tete">
        <div>
          <h2>Administration</h2>
          <div className="sub">{email}</div>
        </div>
        <ActionButton onClick={onDeconnexion} size="sm">
          <LogOut size={14} /> Se déconnecter
        </ActionButton>
      </header>
      <nav className="adm-onglets" aria-label="Sections">
        {ONGLETS.map(([k, libelle]) => (
          <a key={k} href={`#/${k}`} className={k === onglet ? 'on' : ''} aria-current={k === onglet ? 'page' : undefined}>
            {libelle}
          </a>
        ))}
      </nav>
      <main className="adm-corps">
        {onglet === 'serveur' && <Serveur />}
        {onglet === 'comptes' && <Comptes moi={email} />}
        {onglet === 'invitations' && <Invitations />}
        {onglet === 'usage' && <Usage />}
        {onglet === 'radar' && <ActiviteRadar />}
        {onglet === 'corpus' && <Corpus />}
        {onglet === 'couts' && <Couts />}
        {onglet === 'evenements' && <Evenements />}
        {onglet === 'paiements' && <Paiements />}
        {onglet === 'journal' && <Journal />}
      </main>
    </div>
  )
}

// --- Petites briques -----------------------------------------------------------

function Etat({ ok, children, pause }: { ok: boolean; pause?: boolean; children: React.ReactNode }) {
  return (
    <span className={`health ${pause ? 'h-pause' : ok ? 'h-ok' : 'h-err'}`}>
      <span className="hd" />
      {children}
    </span>
  )
}

function Quand({ d }: { d: string | null | undefined }) {
  if (!d) return <span className="muted">jamais</span>
  return <span title={dateHeure(d)}>{relTime(d)}</span>
}

function octets(n: number | null | undefined): string {
  if (n === null || n === undefined) return '?'
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} Go`
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} Mo`
  return `${Math.round(n / 1e3)} Ko`
}

function Chargement<T>({ etat, children }: { etat: { data: T | null; error: string | null; loading: boolean }; children: (d: T) => React.ReactNode }) {
  if (etat.error) return <div className="avertissement">{etat.error}</div>
  if (!etat.data) return <Loading />
  return <>{children(etat.data)}</>
}

/** Un secret montré UNE fois (mot de passe provisoire, lien d'invitation). */
function Secret({ titre, valeur, note, onClose }: { titre: string; valeur: string; note: string; onClose: () => void }) {
  const toast = useToast()
  return (
    <Modal
      title={titre}
      onClose={onClose}
      footer={
        <>
          <ActionButton
            onClick={async () => {
              await navigator.clipboard.writeText(valeur)
              toast('Copié.')
            }}
          >
            <Copy size={14} /> Copier
          </ActionButton>
          <ActionButton variant="primary" onClick={onClose}>
            Fermer
          </ActionButton>
        </>
      }
    >
      <p className="adm-secret mono">{valeur}</p>
      <p className="sub">{note}</p>
    </Modal>
  )
}

// --- Serveur -------------------------------------------------------------------

function Serveur() {
  const etat = useData(() => get<any>('/api/admin/serveur'))
  return (
    <Chargement etat={etat}>
      {(s) => {
        const d = s.deploiement
        const bloque = d.arbreSale === null || d.arbreSale.length > 0
        return (
          <>
            <section className="panel">
              <h4>
                Déploiement automatique
                <span className="hnote">
                  <ActionButton size="sm" onClick={etat.reload}>
                    <RefreshCw size={14} /> Relire
                  </ActionButton>
                </span>
              </h4>
              {bloque ? (
                <div className="avertissement">
                  <b>Déploiement bloqué.</b>{' '}
                  {d.arbreSale === null
                    ? 'git ne peut pas lire le dépôt : aucun déploiement possible.'
                    : `L'arbre de travail n'est pas propre : le code poussé ne sera pas mis en ligne tant que ces fichiers restent.`}
                  {d.arbreSale && d.arbreSale.length > 0 && <pre className="adm-pre">{d.arbreSale.join('\n')}</pre>}
                </div>
              ) : (
                <Etat ok>Arbre propre, rien ne bloque</Etat>
              )}
              {d.enRetard && (
                <div className="avertissement">
                  Le code de <span className="mono">origin/master</span> ({d.commitDistant?.slice(0, 7)}) n'est pas encore en ligne
                  (déployé : {d.commitDeploye?.slice(0, 7)}).
                </div>
              )}
              <dl className="adm-dl">
                <dt>Déployé</dt>
                <dd className="mono">{d.commitDeploye?.slice(0, 7) ?? '?'}</dd>
                <dt>Dépôt</dt>
                <dd className="mono">{d.commitLocal?.slice(0, 7) ?? '?'}</dd>
                <dt>origin/master</dt>
                <dd className="mono">{d.commitDistant?.slice(0, 7) ?? '?'}</dd>
                <dt>Version</dt>
                <dd className="mono">{s.version}</dd>
              </dl>
              <details>
                <summary>Dernières lignes de console-autoupdate</summary>
                <pre className="adm-pre">{d.dernieresLignes.join('\n') || '(rien)'}</pre>
              </details>
            </section>

            <div className="grid3">
              <section className="panel">
                <h4>Services</h4>
                <ul className="adm-liste">
                  {s.services.map((x: any) => (
                    <li key={x.nom}>
                      <span className="mono">{x.nom}</span> <Etat ok={x.etat === 'active'}>{x.etat}</Etat>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="panel">
                <h4>Sauvegarde et extension</h4>
                <dl className="adm-dl">
                  <dt>Vérifiée et rangée par le Pi</dt>
                  <dd>
                    {s.sauvegarde.pi === null ? (
                      <Etat ok={false}>Pi injoignable</Etat>
                    ) : s.sauvegarde.pi.derniereReussite === null ? (
                      <Etat ok={false}>aucune réussite</Etat>
                    ) : (
                      <Etat ok={Date.now() - new Date(s.sauvegarde.pi.derniereReussite).getTime() < 26 * 3_600_000}>
                        <Quand d={s.sauvegarde.pi.derniereReussite} />
                      </Etat>
                    )}
                  </dd>
                  {s.sauvegarde.pi && (
                    <>
                      <dt>Conservées sur le Pi</dt>
                      <dd>
                        {s.sauvegarde.pi.jours.length} jour(s), {octets(s.sauvegarde.pi.octets)}
                        {s.sauvegarde.pi.jours.length > 0 && (
                          <div className="sub">du {s.sauvegarde.pi.jours[0]} au {s.sauvegarde.pi.jours.at(-1)}</div>
                        )}
                      </dd>
                    </>
                  )}
                  <dt>Dernier export servi par le VPS</dt>
                  <dd>
                    <Quand d={s.sauvegarde.derniere} />
                  </dd>
                  <dt>Zip de l'extension {s.extension.version}</dt>
                  <dd>
                    {s.extension.disponible ? (
                      <Etat ok>
                        servi, {octets(s.extension.taille)}, <Quand d={s.extension.empaqueteLe} />
                      </Etat>
                    ) : (
                      <Etat ok={false}>absent</Etat>
                    )}
                  </dd>
                </dl>
                <p className="sub">
                  Au-delà de 26 h sans réussite, le Pi alerte aussi par Telegram. Lu sur le Pi, gardé dix minutes.
                </p>
              </section>
              <section className="panel">
                <h4>Disque et bases</h4>
                <dl className="adm-dl">
                  {s.disque && (
                    <>
                      <dt>Place libre</dt>
                      <dd>
                        <Etat ok={s.disque.libre / s.disque.total > 0.1}>
                          {octets(s.disque.libre)} sur {octets(s.disque.total)}
                        </Etat>
                      </dd>
                    </>
                  )}
                  {s.bases.map((b: any) => (
                    <span key={b.nom} style={{ display: 'contents' }}>
                      <dt className="mono">{b.nom}.db</dt>
                      <dd>{octets(b.octets)}</dd>
                    </span>
                  ))}
                </dl>
              </section>
            </div>
          </>
        )
      }}
    </Chargement>
  )
}

// --- Comptes -------------------------------------------------------------------

interface Compte {
  id: number
  email: string
  formule: string
  role: string
  createdAt: string
  lastLoginAt: string | null
  archivedAt: string | null
  provisoire: boolean
  priseEnMain: boolean
  profileId: number | null
  profil: string | null
  cvDepose: boolean
  sessionsActives: number
  extension: { actif: boolean; createdAt?: string; lastUsedAt?: string | null }
  /** Radar seulement (issue #111). */
  acces?: {
    offert: { depuis: string; motif: string } | null
    abonnement: string | null
    raison: 'offert' | 'abonnement' | 'essai' | 'aucune'
    formule: 'gratuit' | 'radar' | 'complet'
    essaiJusquA: string | null
  }
}

/** Ce qui ouvre Radar à ce compte, en clair (#112, #121). */
function AccesCompte({ a }: { a: NonNullable<Compte['acces']> }) {
  return (
    <div className="sub">
      {a.offert ? (
        <Etat ok>accès offert : {a.offert.motif}</Etat>
      ) : a.raison === 'abonnement' || a.abonnement ? (
        <Etat ok={a.raison !== 'aucune'}>abonnement {a.formule === 'complet' ? 'Complet' : 'Radar'} {a.abonnement}</Etat>
      ) : a.raison === 'essai' ? (
        <Etat ok>essai jusqu'au {fullDate(a.essaiJusquA)}</Etat>
      ) : (
        <Etat ok={false}>sans accès{a.essaiJusquA ? `, essai fini le ${fullDate(a.essaiJusquA)}` : ''}</Etat>
      )}
    </div>
  )
}

function Comptes({ moi }: { moi: string }) {
  const etat = useData(() => get<{ comptes: Compte[]; inscriptions: { ouvertes: boolean; google: boolean } }>('/api/admin/comptes'))
  const [secret, setSecret] = useState<{ titre: string; valeur: string; note: string } | null>(null)
  const [voirArchives, setVoirArchives] = useState(false)
  const toast = useToast()
  return (
    <Chargement etat={etat}>
      {({ comptes, inscriptions }) => {
        const actifs = comptes.filter((c) => !c.archivedAt)
        const visibles = voirArchives ? comptes : actifs
        return (
          <>
            <div className="cstats adm-chiffres">
              {(['radar', 'console', 'admin'] as const).map((f) => (
                <div className="ctile" key={f} style={{ '--h': 220 } as React.CSSProperties}>
                  <div className="cv">{actifs.filter((c) => c.formule === f).length}</div>
                  <div className="cl">comptes {f} actifs</div>
                </div>
              ))}
            </div>
            <section className="panel">
              <h4>Inscriptions dans le temps</h4>
              <CourbeInscriptions comptes={comptes} />
            </section>
            {/* L'inscription libre de Radar (#182) passe par Google : on la
                ferme ici en cas d'abus, sans redéployer. */}
            <div className="filters">
              <span>
                Inscription libre de Radar :{' '}
                {!inscriptions.google ? (
                  <Etat ok={false}>impossible, Google n'est pas configuré</Etat>
                ) : inscriptions.ouvertes ? (
                  <Etat ok>ouverte</Etat>
                ) : (
                  <Etat ok={false}>fermée</Etat>
                )}
              </span>{' '}
              <ActionButton
                size="sm"
                onClick={async () => {
                  await post('/api/admin/inscriptions', { ouvertes: !inscriptions.ouvertes })
                  toast(inscriptions.ouvertes ? 'Inscriptions fermées.' : 'Inscriptions ouvertes.')
                  etat.reload()
                }}
              >
                {inscriptions.ouvertes ? 'Fermer les inscriptions' : 'Ouvrir les inscriptions'}
              </ActionButton>
            </div>
            <NouveauCompte onCree={(r) => { setSecret(r); etat.reload() }} />
            <div className="filters">
              <label>
                <input type="checkbox" checked={voirArchives} onChange={(e) => setVoirArchives(e.target.checked)} /> Montrer les
                comptes archivés ({comptes.length - actifs.length})
              </label>
            </div>
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Adresse</th>
                    <th>Formule</th>
                    <th>Créé</th>
                    <th>Dernière connexion</th>
                    <th>Profil et CV</th>
                    <th>Extension</th>
                    <th>Sessions</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <b>{c.email}</b>
                        <div className="sub">
                          {c.role === 'admin' && 'admin · '}
                          {c.archivedAt ? `archivé ${relTime(c.archivedAt)}` : c.provisoire ? 'mot de passe provisoire' : 'mot de passe choisi'}
                          {c.formule === 'radar' && !c.priseEnMain && ' · prise en main à faire'}
                        </div>
                      </td>
                      <td>
                        {c.formule}
                        {c.acces && <AccesCompte a={c.acces} />}
                      </td>
                      <td><Quand d={c.createdAt} /></td>
                      <td><Quand d={c.lastLoginAt} /></td>
                      <td>
                        {c.profileId === null ? (
                          <span className="muted">aucun</span>
                        ) : (
                          <>
                            {c.profil ?? `#${c.profileId}`} <Etat ok={c.cvDepose}>{c.cvDepose ? 'CV' : 'sans CV'}</Etat>
                          </>
                        )}
                      </td>
                      <td>
                        {c.extension.actif ? (
                          <Etat ok>
                            branchée, dernier envoi <Quand d={c.extension.lastUsedAt} />
                          </Etat>
                        ) : (
                          <span className="muted">non</span>
                        )}
                      </td>
                      <td className="mono">{c.sessionsActives}</td>
                      <td>{!c.archivedAt && <GestesCompte c={c} moi={moi} onFait={etat.reload} onSecret={setSecret} />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {secret && <Secret {...secret} onClose={() => setSecret(null)} />}
          </>
        )
      }}
    </Chargement>
  )
}

function NouveauCompte({ onCree }: { onCree: (s: { titre: string; valeur: string; note: string }) => void }) {
  const [email, setEmail] = useState('')
  const [formule, setFormule] = useState('radar')
  return (
    <section className="panel">
      <h4>Créer un compte</h4>
      <p className="sub">
        Mot de passe provisoire, à changer à la première connexion. Pour un invité, préférer un lien d'invitation : le secret ne
        voyage pas.
      </p>
      <div className="filters adm-form">
        <input className="input-field" type="email" placeholder="adresse@exemple.be" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select className="input-field" value={formule} onChange={(e) => setFormule(e.target.value)}>
          <option value="radar">radar</option>
          <option value="console">console</option>
        </select>
        <ActionButton
          variant="primary"
          disabled={!email}
          onClick={async () => {
            const r = await post<{ email: string; motDePasse: string }>('/api/admin/comptes', { email, formule })
            setEmail('')
            onCree({ titre: `Compte ${formule} créé : ${r.email}`, valeur: r.motDePasse, note: 'Mot de passe provisoire, montré une seule fois.' })
          }}
        >
          Créer
        </ActionButton>
      </div>
    </section>
  )
}

function GestesCompte({
  c,
  moi,
  onFait,
  onSecret,
}: {
  c: Compte
  moi: string
  onFait: () => void
  onSecret: (s: { titre: string; valeur: string; note: string }) => void
}) {
  const toast = useToast()
  const [reinit, setReinit] = useState<any | null>(null)
  const [archiver, setArchiver] = useState(false)
  const [offrir, setOffrir] = useState<string | null>(null)
  const [temps, setTemps] = useState<{ jours: string; jusqua: string; motif: string } | null>(null)
  const url = (g: string) => `/api/admin/comptes/${c.id}/${g}`
  return (
    <div className="adm-gestes">
      {c.acces &&
        (c.acces.offert ? (
          <ActionButton
            size="sm"
            onClick={async () => {
              await post(url('acces-offert'), { offert: false })
              toast(`Accès offert retiré à ${c.email}.`)
              onFait()
            }}
          >
            Retirer l'accès offert
          </ActionButton>
        ) : (
          <>
            <ActionButton size="sm" onClick={() => setTemps({ jours: '30', jusqua: '', motif: '' })}>
              Offrir du temps
            </ActionButton>
            <ActionButton size="sm" onClick={() => setOffrir('testeur de la beta')}>
              Offrir à vie
            </ActionButton>
          </>
        ))}
      {temps !== null && (
        <Modal
          title={`Offrir du temps à ${c.email}`}
          onClose={() => setTemps(null)}
          footer={
            <>
              <ActionButton onClick={() => setTemps(null)}>Annuler</ActionButton>
              <ActionButton
                variant="primary"
                disabled={!temps.jusqua && !(Number(temps.jours) > 0)}
                onClick={async () => {
                  const r = await post<{ jusqua: string }>(url('temps-offert'), temps.jusqua ? { jusqua: temps.jusqua, motif: temps.motif } : { jours: Number(temps.jours), motif: temps.motif })
                  setTemps(null)
                  toast(`Radar gratuit pour ${c.email} jusqu'au ${fullDate(r.jusqua)}.`)
                  onFait()
                }}
              >
                Offrir
              </ActionButton>
            </>
          }
        >
          <p>
            Fin actuelle : {c.acces?.essaiJusquA ? fullDate(c.acces.essaiJusquA) : 'inconnue'}. Les jours s'ajoutent à cette fin, ou à aujourd'hui si elle est passée. Une date remplace la fin.
          </p>
          <label className="field-label">
            Jours de plus
            <input className="input-field" type="number" min={1} value={temps.jours} onChange={(e) => setTemps({ ...temps, jours: e.target.value, jusqua: '' })} />
          </label>
          <label className="field-label">
            Ou jusqu'au
            <input className="input-field" type="date" value={temps.jusqua} onChange={(e) => setTemps({ ...temps, jusqua: e.target.value })} />
          </label>
          <label className="field-label">
            Motif (facultatif)
            <input className="input-field" value={temps.motif} onChange={(e) => setTemps({ ...temps, motif: e.target.value })} />
          </label>
        </Modal>
      )}
      {offrir !== null && (
        <Modal
          title={`Offrir Radar à ${c.email}`}
          onClose={() => setOffrir(null)}
          footer={
            <>
              <ActionButton onClick={() => setOffrir(null)}>Annuler</ActionButton>
              <ActionButton
                variant="primary"
                disabled={!offrir.trim()}
                onClick={async () => {
                  await post(url('acces-offert'), { motif: offrir })
                  setOffrir(null)
                  toast(`Radar offert à ${c.email}.`)
                  onFait()
                }}
              >
                Offrir
              </ActionButton>
            </>
          }
        >
          <p>Radar complet, sans abonnement ni fin, jusqu'à ce qu'on le retire. Le motif reste dans le journal.</p>
          <input className="input-field" value={offrir} onChange={(e) => setOffrir(e.target.value)} aria-label="Motif" />
        </Modal>
      )}
      <ActionButton
        size="sm"
        onClick={async () => {
          const r = await post<{ motDePasse: string }>(url('mot-de-passe'))
          onSecret({ titre: `Mot de passe provisoire de ${c.email}`, valeur: r.motDePasse, note: 'Ses sessions sont tombées. Montré une seule fois.' })
          onFait()
        }}
      >
        Provisoire
      </ActionButton>
      <ActionButton
        size="sm"
        disabled={c.sessionsActives === 0}
        onClick={async () => {
          const r = await post<{ sessions: number }>(url('sessions'))
          toast(`${r.sessions} session(s) fermée(s).`)
          onFait()
        }}
      >
        Déconnecter
      </ActionButton>
      <ActionButton
        size="sm"
        disabled={!c.extension.actif}
        onClick={async () => {
          await post(url('extension'))
          toast('Jeton de l’extension révoqué.')
          onFait()
        }}
      >
        Couper l'extension
      </ActionButton>
      {(c.formule === 'radar' || c.formule === 'console') && (
        <ActionButton size="sm" onClick={async () => setReinit(await post(url('reinitialiser'), { essai: true }))}>
          Rejouer l'accès
        </ActionButton>
      )}
      {c.email !== moi && (
        <ActionButton size="sm" onClick={() => setArchiver(true)}>
          Archiver
        </ActionButton>
      )}
      {reinit && (
        <Modal
          title={`Rejouer le premier accès de ${c.email}`}
          onClose={() => setReinit(null)}
          footer={
            <>
              <ActionButton onClick={() => setReinit(null)}>Annuler</ActionButton>
              <ActionButton
                variant="primary"
                onClick={async () => {
                  const r = await post<{ motDePasse: string }>(url('reinitialiser'), { essai: false })
                  setReinit(null)
                  onSecret({ titre: `${c.email} remis à zéro`, valeur: r.motDePasse, note: 'Mot de passe provisoire, montré une seule fois.' })
                  onFait()
                }}
              >
                Archiver et remettre à zéro
              </ActionButton>
            </>
          }
        >
          <p>
            Ce qui sera archivé (rien n'est effacé) : profil {reinit.bilan.profil ?? 'aucun'}, {reinit.bilan.dossiers} dossier(s),{' '}
            {reinit.bilan.candidatures} candidature(s), {reinit.bilan.offresApportees} offre(s) apportée(s) ; {reinit.bilan.abonnements}{' '}
            abonnement(s) retiré(s) ; {reinit.bilan.demandes} demande(s) de site close(s).
          </p>
          <p className="sub">Le compte redevient celui d'un invité qui n'a encore rien fait, avec un mot de passe provisoire neuf.</p>
        </Modal>
      )}
      {archiver && (
        <Modal
          title={`Archiver ${c.email}`}
          onClose={() => setArchiver(false)}
          footer={
            <>
              <ActionButton onClick={() => setArchiver(false)}>Annuler</ActionButton>
              <ActionButton
                variant="primary"
                onClick={async () => {
                  await post(url('archiver'))
                  setArchiver(false)
                  toast(`${c.email} archivé.`)
                  onFait()
                }}
              >
                Archiver
              </ActionButton>
            </>
          }
        >
          <p>Le compte ne pourra plus se connecter. Ses données restent en base ; rien n'est effacé.</p>
        </Modal>
      )}
    </div>
  )
}

/** Un point par jour de création : vingt comptes créés le même jour en lot ne
 *  font qu'une marche, pas vingt points empilés. */
function parJour(dates: number[]): { t: number; cumul: number; n: number }[] {
  const jours: { t: number; cumul: number; n: number }[] = []
  dates.forEach((t, i) => {
    const dernier = jours.at(-1)
    if (dernier && new Date(dernier.t).toDateString() === new Date(t).toDateString()) {
      dernier.cumul = i + 1
      dernier.n++
    } else jours.push({ t, cumul: i + 1, n: 1 })
  })
  return jours
}

/**
 * Le cumul des comptes créés, une ligne par formule. Deux séries sur un seul
 * axe, une légende, un libellé direct en bout de ligne, le détail au survol
 * (`<title>` de chaque point). Les couleurs sont celles du sens déjà posé dans
 * l'habillage (`--brand`, `--teal-ink`) : aucune couleur nouvelle.
 */
function CourbeInscriptions({ comptes }: { comptes: Compte[] }) {
  const series = (['radar', 'console'] as const).map((f) => ({
    f,
    couleur: f === 'radar' ? 'var(--teal-ink)' : 'var(--brand)',
    dates: comptes.filter((c) => c.formule === f).map((c) => new Date(c.createdAt.replace(' ', 'T') + 'Z').getTime()).sort((a, b) => a - b),
  }))
  const toutes = series.flatMap((s) => s.dates)
  if (toutes.length === 0) return <Empty>Aucun compte.</Empty>
  const debut = Math.min(...toutes)
  const fin = Date.now()
  const max = Math.max(...series.map((s) => s.dates.length), 1)
  const L = 640
  const H = 160
  const G = 36
  const x = (t: number) => G + ((t - debut) / Math.max(fin - debut, 1)) * (L - G - 70)
  const y = (n: number) => H - 20 - (n / max) * (H - 36)
  const jour = (t: number) => new Date(t).toLocaleDateString('fr-BE')
  return (
    <figure className="adm-courbe">
      <svg viewBox={`0 0 ${L} ${H}`} role="img" aria-label="Cumul des comptes créés par formule">
        <line x1={G} x2={L - 70} y1={y(0)} y2={y(0)} className="adm-axe" />
        <text x={G - 6} y={y(max) + 4} textAnchor="end" className="adm-lbl">{max}</text>
        <text x={G - 6} y={y(0) + 4} textAnchor="end" className="adm-lbl">0</text>
        <text x={G} y={H - 4} className="adm-lbl">{jour(debut)}</text>
        <text x={L - 70} y={H - 4} textAnchor="end" className="adm-lbl">aujourd'hui</text>
        {series.map((s) => {
          if (s.dates.length === 0) return null
          // En escalier : un compte arrive à une date, il ne se crée pas en pente.
          let d = `M ${x(debut)} ${y(0)}`
          for (const p of parJour(s.dates)) d += ` H ${x(p.t)} V ${y(p.cumul)}`
          d += ` H ${x(fin)}`
          return (
            <g key={s.f}>
              <path d={d} fill="none" stroke={s.couleur} strokeWidth={2} />
              {parJour(s.dates).map(({ t, cumul, n }) => (
                <circle key={t} cx={x(t)} cy={y(cumul)} r={4} fill={s.couleur} stroke="var(--panel)" strokeWidth={2}>
                  <title>{`${s.f} : ${n} compte(s) créé(s) le ${jour(t)}, ${cumul} au total`}</title>
                </circle>
              ))}
              <text x={x(fin) + 6} y={y(s.dates.length) + 4} className="adm-lbl">{`${s.f} ${s.dates.length}`}</text>
            </g>
          )
        })}
      </svg>
      <figcaption className="adm-legende">
        {series.map((s) => (
          <span key={s.f}>
            <i style={{ background: s.couleur }} /> {s.f}
          </span>
        ))}
        <span className="muted">archivés compris</span>
      </figcaption>
    </figure>
  )
}

// --- Invitations ---------------------------------------------------------------

function Invitations() {
  const etat = useData(() => get<{ invitations: any[] }>('/api/admin/invitations'))
  const [email, setEmail] = useState('')
  const [formule, setFormule] = useState('radar')
  const [lien, setLien] = useState<{ titre: string; valeur: string; note: string } | null>(null)
  // Le lien part aussi par mail (lot 3), dans la langue choisie. Décoché, il
  // s'affiche seulement, comme avant, à transmettre soi-même.
  const [parMail, setParMail] = useState(true)
  const [langue, setLangue] = useState('fr')
  const toast = useToast()
  const inviter = async (adresse: string, f: string) => {
    const r = await post<{ lien: string; expiresAt: string; envoye: boolean | null; erreur?: string }>('/api/admin/invitations', {
      email: adresse,
      formule: f,
      envoyer: parMail,
      lang: langue,
    })
    const envoi =
      r.envoye === true ? ' Le mail est parti.' : r.envoye === false ? ` Le mail n'est PAS parti (${r.erreur ?? 'erreur'}) : transmettez le lien vous-même.` : ''
    setLien({ titre: `Invitation ${f} pour ${adresse}`, valeur: r.lien, note: `À usage unique, valable jusqu'au ${dateHeure(r.expiresAt)}. Une nouvelle invitation annule celle-ci.${envoi}` })
    etat.reload()
  }
  return (
    <>
      <section className="panel">
        <h4>Inviter</h4>
        <div className="filters adm-form">
          <input className="input-field" type="email" placeholder="adresse@exemple.be" value={email} onChange={(e) => setEmail(e.target.value)} />
          <select className="input-field" value={formule} onChange={(e) => setFormule(e.target.value)}>
            <option value="radar">radar</option>
            <option value="console">console</option>
          </select>
          <label className="adm-coche">
            <input type="checkbox" checked={parMail} onChange={(e) => setParMail(e.target.checked)} /> Envoyer par mail
          </label>
          {parMail && (
            <select className="input-field" value={langue} onChange={(e) => setLangue(e.target.value)} aria-label="Langue du mail">
              <option value="fr">en français</option>
              <option value="en">in English</option>
            </select>
          )}
          <ActionButton variant="primary" disabled={!email} onClick={async () => { await inviter(email, formule); setEmail('') }}>
            {parMail ? 'Inviter par mail' : 'Générer le lien'}
          </ActionButton>
        </div>
      </section>
      <Chargement etat={etat}>
        {({ invitations }) =>
          invitations.length === 0 ? (
            <Empty>Aucune invitation émise.</Empty>
          ) : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Adresse</th>
                    <th>Formule</th>
                    <th>État</th>
                    <th>Émise</th>
                    <th>Expire</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((i, n) => (
                    <tr key={n}>
                      <td>{i.email}</td>
                      <td>{i.formule}</td>
                      <td>
                        <Etat ok={i.etat === 'acceptee' || i.etat === 'en_attente'} pause={i.etat === 'en_attente'}>
                          {i.etat === 'acceptee' ? <>acceptée <Quand d={i.acceptedAt} /></> : i.etat.replace('_', ' ')}
                        </Etat>
                      </td>
                      <td><Quand d={i.createdAt} /></td>
                      <td>{dateHeure(i.expiresAt)}</td>
                      <td className="adm-gestes">
                        {i.etat !== 'acceptee' && (
                          <ActionButton size="sm" onClick={() => inviter(i.email, i.formule)}>Réémettre</ActionButton>
                        )}
                        {i.etat === 'en_attente' && (
                          <ActionButton
                            size="sm"
                            onClick={async () => {
                              await post('/api/admin/invitations/revoquer', { email: i.email, formule: i.formule })
                              toast('Invitation révoquée.')
                              etat.reload()
                            }}
                          >
                            Révoquer
                          </ActionButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Chargement>
      {lien && <Secret {...lien} onClose={() => setLien(null)} />}
    </>
  )
}

// --- Activité Radar ------------------------------------------------------------

function ActiviteRadar() {
  const etat = useData(() => get<any>('/api/admin/radar'))
  return (
    <Chargement etat={etat}>
      {(r) => (
        <>
          <section className="panel">
            <h4>Par compte</h4>
            <p className="sub">
              Les e-mails d'alerte n'existent pas encore : rien à compter. Plafond : {r.plafond} pages suivies par compte, au-delà
              l'abonnement est refusé ; un compte qui le dépassait avant la règle garde ses pages.
            </p>
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Compte</th>
                    <th>Pages suivies</th>
                    <th>Offres évaluées</th>
                    <th>En pile</th>
                    <th>Apportées par l'extension</th>
                    <th>Demandes de site</th>
                    <th>Dernière activité</th>
                  </tr>
                </thead>
                <tbody>
                  {r.activite.map((a: any) => (
                    <tr key={a.userId}>
                      <td>
                        <a href={`#/evenements?profil=${a.profileId ?? ''}&email=${encodeURIComponent(a.email)}`}>{a.email}</a>
                        {a.profileId === null && <div className="sub">pas encore de CV</div>}
                      </td>
                      <td className="mono">
                        <Etat ok={a.suivis <= a.plafond} pause={a.suivis === 0}>{a.suivis} / {a.plafond}</Etat>
                      </td>
                      <td className="mono">{a.scorees}</td>
                      <td className="mono">{a.enPile}</td>
                      <td className="mono">{a.apportees}</td>
                      <td className="mono">{a.demandes}</td>
                      <td><Quand d={a.derniereActivite} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section className="panel">
            <h4>Demandes de nouveau site <span className="hnote">engagement : {r.delaiDemandeJours} jours</span></h4>
            {r.demandes.length === 0 ? (
              <Empty>Aucune demande.</Empty>
            ) : (
              <div className="tblwrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Site</th>
                      <th>Demandeur</th>
                      <th>État</th>
                      <th>Délai</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.demandes.map((d: any) => (
                      <tr key={d.id}>
                        <td className="adm-url"><a href={d.url} target="_blank" rel="noreferrer">{d.url}</a></td>
                        <td>{d.requestedByEmail}</td>
                        <td>
                          <Etat ok={d.status !== 'refusee'} pause={d.status === 'en_attente'}>
                            {d.status === 'en_attente' ? 'en attente' : d.status === 'active' ? 'acceptée' : 'refusée'}
                          </Etat>
                        </td>
                        <td>
                          {d.enRetard ? <Etat ok={false}>{d.jours} j, en retard</Etat> : `${d.jours} j`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="sub">Répondre en ligne de commande : source-request-activate ou source-request-refuse.</p>
          </section>
        </>
      )}
    </Chargement>
  )
}

// --- Corpus et runs ------------------------------------------------------------

function Corpus() {
  const etat = useData(() => get<any>('/api/admin/corpus'))
  return (
    <Chargement etat={etat}>
      {(c) => (
        <>
          {(['radar', 'console'] as const).map((f) => {
            const x = c[f]
            const pannes = x.sources.filter((s: any) => s.statut === 'error' || s.archivageSuspendu)
            return (
              <section className="panel" key={f}>
                <h4>{f === 'radar' ? 'Radar' : 'Console'}</h4>
                <dl className="adm-dl">
                  <dt>Dernier run</dt>
                  <dd>
                    {x.run.log ? (
                      <Etat ok={x.run.log.ok}>
                        {x.run.log.ok ? 'réussi' : 'en échec'}, {x.run.log.secondes} s, {x.run.log.quand}
                      </Etat>
                    ) : (
                      <span className="muted">log introuvable</span>
                    )}
                  </dd>
                  <dt>Bilan</dt>
                  <dd>
                    {x.run.bilan
                      ? `${x.run.bilan.newOffers ?? 0} nouvelle(s), ${x.run.bilan.expiredOffers ?? 0} expirée(s), ${x.run.bilan.pairsScored ?? 0} évaluation(s)`
                      : 'aucun run enregistré'}{' '}
                    {x.run.termineA && <Quand d={x.run.termineA} />}
                  </dd>
                  <dt>Sources</dt>
                  <dd>
                    {x.sources.length} active(s){pannes.length > 0 && <>, <Etat ok={false}>{pannes.length} à regarder</Etat></>}
                  </dd>
                  <dt>Catégorisation échouée</dt>
                  <dd>{x.categorisationsEchouees.length} offre(s)</dd>
                </dl>
                <details open={pannes.length > 0}>
                  <summary>Santé des sources</summary>
                  <div className="tblwrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Source</th>
                          <th>État</th>
                          <th>Dernière collecte</th>
                          <th>Offres actives</th>
                          {f === 'radar' && <th>Suivie par</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {x.sources.map((s: any) => (
                          <tr key={s.id}>
                            <td>
                              {s.nom} <span className="sub mono">{s.plateforme}</span>
                            </td>
                            <td>
                              {s.statut === 'error' ? (
                                <Etat ok={false}>
                                  en panne depuis <Quand d={s.enPanneDepuis} />
                                </Etat>
                              ) : s.archivageSuspendu ? (
                                <Etat ok={false}>archivage suspendu</Etat>
                              ) : (
                                <Etat ok pause={s.statut === 'paused'}>{s.statut}</Etat>
                              )}
                              {s.derniereErreur && s.statut === 'error' && <div className="sub">{s.derniereErreur}</div>}
                            </td>
                            <td><Quand d={s.derniereCollecte} /></td>
                            <td className="mono">{s.offresActives}</td>
                            {f === 'radar' && <td className="mono">{s.suivis}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
                {x.categorisationsEchouees.length > 0 && (
                  <details>
                    <summary>Offres dont la catégorisation a échoué</summary>
                    <ul className="adm-liste">
                      {x.categorisationsEchouees.map((o: any) => (
                        <li key={o.offerId}>
                          #{o.offerId} {o.titre} <span className="sub">({o.source}, {o.tentatives} essai(s) : {o.raison})</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </section>
            )
          })}
        </>
      )}
    </Chargement>
  )
}

// --- Appels IA -----------------------------------------------------------------

function Couts() {
  const etat = useData(() => get<any>('/api/admin/couts'))
  return <Chargement etat={etat}>{(c) => <AnalyseCouts c={c} />}</Chargement>
}

const usd = (n: number) => (n >= 1 ? `${n.toFixed(2)} $` : n >= 0.01 ? `${n.toFixed(3)} $` : `${n.toFixed(4)} $`)
const eur = (n: number, taux: number) => `${(n * taux).toFixed(2)} €`
const pct = (n: number) => `${Math.round(n * 100)} %`
const milliers = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)} M` : n >= 1e3 ? `${Math.round(n / 1e3)} k` : String(n))

function AnalyseCouts({ c }: { c: any }) {
  const r = c.reel
  const taux = r.usdEur
  return (
    <>
      {r.depuis === null ? (
        <div className="avertissement">
          Aucun appel journalisé pour l'instant : le journal des appels IA commence avec ce déploiement. Les chiffres apparaîtront au
          premier appel (une évaluation, une catégorisation, un dossier), au plus tard à la nuit prochaine.
        </div>
      ) : (
        <p className="sub">
          Coût réel calculé sur les jetons annoncés par l'API, journalisé depuis le {dateHeure(r.depuis)}. Tarifs OpenAI du code
          (services/coutIa.ts), taux indicatif 1 $ = {taux} €. À confronter à la facture OpenAI.
        </p>
      )}
      {r.modelesSansTarif.length > 0 && (
        <div className="avertissement">
          <b>Coût incomplet.</b> Modèle(s) sans tarif connu, leur coût manque aux totaux : {r.modelesSansTarif.join(', ')}. L'ajouter
          dans TARIFS.
        </div>
      )}
      <div className="cstats adm-tuiles">
        {r.totaux.map((t: any) => (
          <div className="ctile" key={t.periode} style={{ '--h': 220 } as React.CSSProperties}>
            <div className="cv">{usd(t.coutUsd)}</div>
            <div className="cl">
              {t.periode} · {eur(t.coutUsd, taux)} · {t.appels} appel(s){t.echecs > 0 && `, ${t.echecs} échec(s)`}
            </div>
          </div>
        ))}
        <div className="ctile" style={{ '--h': 220 } as React.CSSProperties}>
          <div className="cv">{usd(r.projectionMoisUsd)}</div>
          <div className="cl">Projection du mois · {eur(r.projectionMoisUsd, taux)}</div>
        </div>
      </div>

      <section className="panel">
        <h4>Coût par jour, 30 derniers jours</h4>
        <BarresParJour jours={r.parJour} />
      </section>

      <section className="panel">
        <h4>
          Par compte Radar, 30 jours
          <span className="hnote">prix du compte : {r.prixRadarEur} € par mois</span>
        </h4>
        <p className="sub">
          Ce qu'un compte coûte en IA face à ce qu'il paierait. Le corpus (catégorisation des offres, nuit) n'est imputé à
          personne : {usd(r.nonImputeUsd)} ({eur(r.nonImputeUsd, taux)}) sur la période, à répartir sur tous les comptes.
          {r.parCompte.some((x: any) => x.appels === 0) &&
            ` ${r.parCompte.filter((x: any) => x.appels === 0).length} compte(s) actif(s) sans aucun appel, non listés.`}
        </p>
        <TableIa
          colonnes={['Compte', 'Appels', 'Coût', 'En euros', 'Part du prix']}
          lignes={r.parCompte.filter((x: any) => x.appels > 0).map((x: any) => [
            x.email,
            x.appels,
            usd(x.coutUsd),
            eur(x.coutUsd, taux),
            <Etat key="p" ok={x.partDuPrix < 0.5} pause={x.appels === 0}>{pct(x.partDuPrix)}</Etat>,
          ])}
        />
      </section>

      <TableAgregat titre="Par opération, 30 jours" note="Ce qui coûte, et combien par appel : c'est là qu'un prompt trop long se voit." lignes={r.parOperation} />
      <TableAgregat titre="Par modèle, 30 jours" note={`Part de l'entrée servie par le cache de prompt : ${pct(r.tauxCache)}.`} lignes={r.parModele} />
      <TableAgregat titre="Par origine, 30 jours" note="La nuit, une commande, ou la route qui a déclenché l'appel." lignes={r.parOrigine} />
      <TableAgregat titre="Par formule, 30 jours" lignes={r.parFormule} />

      {r.echecsRecents.length > 0 && (
        <section className="panel">
          <h4>Échecs récents</h4>
          <TableIa
            colonnes={['Quand', 'Formule', 'Opération', 'Modèle', 'Nature', 'Origine']}
            lignes={r.echecsRecents.map((e: any) => [<Quand key="q" d={e.createdAt} />, e.formule, e.operation, e.model, e.erreur ?? '?', e.origine ?? ''])}
          />
        </section>
      )}

      <details className="panel">
        <summary>Avant le journal : appels déduits de ce qu'ils ont produit</summary>
        <p className="sub">
          Ce qui précède le journal ne se chiffre pas : on ne compte que les scores, catégories et dossiers écrits. Modèles :
          scoring et catégorisation <span className="mono">{c.modeles.scoring}</span>, adaptation{' '}
          <span className="mono">{c.modeles.adaptation}</span>.
        </p>
        <TableIa
          colonnes={['Jour', 'Formule', 'Scoring', 'Catégorisation', 'Adaptation']}
          lignes={c.appels.map((a: any) => [a.jour, a.formule, a.scoring, a.categorisation, a.adaptation])}
        />
      </details>
    </>
  )
}

function TableIa({ colonnes, lignes }: { colonnes: string[]; lignes: React.ReactNode[][] }) {
  if (lignes.length === 0) return <Empty>Rien sur la période.</Empty>
  return (
    <div className="tblwrap">
      <table className="tbl">
        <thead>
          <tr>{colonnes.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={i}>{l.map((v, j) => <td key={j} className={j > 0 && typeof v !== 'object' ? 'mono' : undefined}>{v}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TableAgregat({ titre, note, lignes }: { titre: string; note?: string; lignes: any[] }) {
  return (
    <section className="panel">
      <h4>{titre}</h4>
      {note && <p className="sub">{note}</p>}
      <TableIa
        colonnes={['', 'Appels', 'Échecs', 'Entrée / appel', 'Sortie / appel', 'Jetons', 'Durée moy. / max', 'Coût', 'Par appel']}
        lignes={lignes.map((a) => [
          <b key="c">{a.cle}</b>,
          a.appels,
          a.echecs > 0 ? <Etat key="e" ok={false}>{a.echecs} ({pct(a.echecs / a.appels)})</Etat> : '0',
          milliers(Math.round(a.entree / Math.max(a.appels, 1))),
          milliers(Math.round(a.sortie / Math.max(a.appels, 1))),
          `${milliers(a.entree)} / ${milliers(a.sortie)}`,
          `${(a.msMoyen / 1000).toFixed(1)} s / ${(a.msMax / 1000).toFixed(1)} s`,
          a.sansTarif > 0 ? `${usd(a.coutUsd)} (incomplet)` : usd(a.coutUsd),
          usd(a.coutUsd / Math.max(a.appels, 1)),
        ])}
      />
    </section>
  )
}

/** Le coût par jour, une barre par jour (les deux formules additionnées), le
 *  détail au survol. Une seule série : pas de légende, le titre la nomme. */
function BarresParJour({ jours }: { jours: any[] }) {
  const parJour = new Map<string, { total: number; detail: string[] }>()
  for (const j of jours) {
    const d = parJour.get(j.cle) ?? { total: 0, detail: [] }
    d.total += j.coutUsd
    d.detail.push(`${j.formule} ${usd(j.coutUsd)}, ${j.appels} appel(s)`)
    parJour.set(j.cle, d)
  }
  const liste = [...Array(30)].map((_, i) => {
    const d = new Date(Date.now() - (29 - i) * 86_400_000).toISOString().slice(0, 10)
    return { jour: d, ...(parJour.get(d) ?? { total: 0, detail: [] }) }
  })
  const max = Math.max(...liste.map((l) => l.total), 0)
  if (max === 0) return <Empty>Aucun coût journalisé sur la période.</Empty>
  const L = 640
  const H = 150
  const G = 50
  const largeur = (L - G) / liste.length
  return (
    <figure className="adm-courbe">
      <svg viewBox={`0 0 ${L} ${H}`} role="img" aria-label="Coût IA par jour sur 30 jours">
        <line x1={G} x2={L} y1={H - 18} y2={H - 18} className="adm-axe" />
        <text x={G - 6} y={14} textAnchor="end" className="adm-lbl">{usd(max)}</text>
        <text x={G - 6} y={H - 14} textAnchor="end" className="adm-lbl">0</text>
        <text x={G} y={H - 2} className="adm-lbl">{liste[0].jour.slice(5)}</text>
        <text x={L} y={H - 2} textAnchor="end" className="adm-lbl">aujourd'hui</text>
        {liste.map((l, i) => {
          const h = (l.total / max) * (H - 30)
          return (
            <g key={l.jour}>
              {/* Zone de survol plus large que la barre, sur toute la hauteur. */}
              <rect x={G + i * largeur} y={0} width={largeur} height={H - 18} fill="transparent">
                <title>{`${l.jour} : ${usd(l.total)}${l.detail.length ? ` (${l.detail.join(' ; ')})` : ''}`}</title>
              </rect>
              {h > 0 && (
                <rect x={G + i * largeur + 2} y={H - 18 - h} width={Math.max(largeur - 4, 2)} height={h} rx={3} fill="var(--brand)" pointerEvents="none" />
              )}
            </g>
          )
        })}
      </svg>
    </figure>
  )
}

// --- Événements ----------------------------------------------------------------

function filtresDeLAdresse(): { profil: string; email: string } {
  const q = new URLSearchParams(window.location.hash.split('?')[1] ?? '')
  return { profil: q.get('profil') ?? '', email: q.get('email') ?? '' }
}

function Evenements() {
  const depart = filtresDeLAdresse()
  const [formule, setFormule] = useState('radar')
  const [profil, setProfil] = useState(depart.profil)
  const [email, setEmail] = useState(depart.email)
  const [type, setType] = useState('')
  const comptes = useData(() => get<{ comptes: Compte[] }>('/api/admin/comptes'))
  const q = new URLSearchParams({ formule, limite: '200' })
  if (profil) q.set('profil', profil)
  if (email) q.set('email', email)
  if (type) q.set('type', type)
  const etat = useData(() => get<{ evenements: any[] }>(`/api/admin/evenements?${q}`), [q.toString()])
  const avecProfil = (comptes.data?.comptes ?? []).filter((c) => c.formule === formule && c.profileId !== null)
  return (
    <>
      <div className="filters adm-form">
        <select className="input-field" value={formule} onChange={(e) => { setFormule(e.target.value); setProfil(''); setEmail('') }}>
          <option value="radar">radar.db</option>
          <option value="console">console.db</option>
        </select>
        <select
          className="input-field"
          value={profil}
          onChange={(e) => {
            const c = avecProfil.find((x) => String(x.profileId) === e.target.value)
            setProfil(e.target.value)
            setEmail(c?.email ?? '')
          }}
        >
          <option value="">Tous les comptes</option>
          {avecProfil.map((c) => (
            <option key={c.id} value={String(c.profileId)}>{c.email}</option>
          ))}
        </select>
        <input className="input-field" placeholder="Type (préfixe : offer., source.)" value={type} onChange={(e) => setType(e.target.value)} />
      </div>
      <Chargement etat={etat}>
        {({ evenements }) =>
          evenements.length === 0 ? (
            <Empty>Aucun événement.</Empty>
          ) : (
            <ul className="adm-liste adm-evts">
              {evenements.map((e) => (
                <li key={e.id}>
                  <span className="mono sub" title={dateHeure(e.createdAt)}>{e.createdAt}</span> <b>{e.type}</b>{' '}
                  <span className="sub">
                    {e.entityType} #{e.entityId}, {e.author}
                  </span>
                  {e.payload !== null && <code className="adm-charge">{JSON.stringify(e.payload)}</code>}
                </li>
              ))}
            </ul>
          )
        }
      </Chargement>
    </>
  )
}

// --- Journal -------------------------------------------------------------------

/** Ce que Stripe a envoyé (issue #110). Un échec ou un événement resté
 *  « reçu » est un abonnement que Radar ne reflète pas : il se voit en tête. */
function Paiements() {
  const etat = useData(() =>
    get<{
      evenements: any[]
      alertes: { echecs: number; enSuspens: number; refus: number }
      secretConfigure: boolean
      abonnements: {
        parStatut: Record<string, number>
        payants: number
        essaisTermines: number
        essaisConvertis: number
        resiliations: number
        fins: number
      }
      prix: { montant: number; devise: string; intervalle: string } | null
    }>('/api/admin/stripe')
  )
  return (
    <Chargement etat={etat}>
      {({ evenements, alertes, secretConfigure, abonnements: a, prix }) => (
        <>
          {/* La formule payante en un coup d'œil (#117), sur 30 jours. */}
          <div className="filters">
            <span>
              <b>{a.payants}</b> payant(s){prix ? <>, {((a.payants * prix.montant) / 100).toLocaleString('fr-BE', { style: 'currency', currency: prix.devise.toUpperCase() })} par {prix.intervalle === 'year' ? 'an' : 'mois'} au moins (au prix de Radar mensuel)</> : null}
            </span>{' '}
            <span className="sub">
              essai {a.parStatut.essai}, actif {a.parStatut.actif}, en retard {a.parStatut.en_retard}, résilié {a.parStatut.resilie}, expiré {a.parStatut.expire}
            </span>
          </div>
          <div className="filters">
            <span className="sub">
              30 jours : {a.essaisConvertis} essai(s) converti(s) sur {a.essaisTermines}, {a.resiliations} résiliation(s), {a.fins} fin(s)
            </span>{' '}
            <Etat ok={a.parStatut.en_retard === 0}>{a.parStatut.en_retard} paiement(s) en échec</Etat>
          </div>
          <div className="filters">
            <Etat ok={secretConfigure}>{secretConfigure ? 'secret du webhook configuré' : 'STRIPE_WEBHOOK_SECRET absent : le webhook répond 503'}</Etat>{' '}
            <Etat ok={alertes.echecs === 0}>{alertes.echecs} échec(s) sur 7 jours</Etat>{' '}
            <Etat ok={alertes.enSuspens === 0}>{alertes.enSuspens} en suspens</Etat>{' '}
            <span className="sub">{alertes.refus} appel(s) refusé(s) (signature)</span>
          </div>
          {evenements.length === 0 ? (
            <Empty>Aucun événement Stripe reçu pour l'instant.</Empty>
          ) : (
            <ul className="adm-liste adm-evts">
              {evenements.map((e) => (
                <li key={e.id}>
                  <span className="mono sub">{e.recuAt}</span>{' '}
                  <Etat ok={e.statut === 'traite' || e.statut === 'ignore'}>{e.statut}</Etat> <b>{e.type ?? 'appel non signé'}</b>{' '}
                  {e.eventId && <span className="mono sub">{e.eventId}</span>}
                  {e.detail && <div className="sub">{e.detail}</div>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Chargement>
  )
}

function Journal() {
  const etat = useData(() => get<{ journal: any[] }>('/api/admin/journal'))
  return (
    <Chargement etat={etat}>
      {({ journal }) =>
        journal.length === 0 ? (
          <Empty>Aucun geste d'administration pour l'instant.</Empty>
        ) : (
          <ul className="adm-liste adm-evts">
            {journal.map((j) => (
              <li key={j.id}>
                <span className="mono sub">{j.createdAt}</span> <b>{j.action}</b> {j.cible && <>sur {j.cible}</>}{' '}
                <span className="sub">par {j.auteur}</span>
                {j.details !== null && <code className="adm-charge">{JSON.stringify(j.details)}</code>}
              </li>
            ))}
          </ul>
        )
      }
    </Chargement>
  )
}
